/**
 * SWMMarkActivityLinker — Manual + Cognara-driven mark ↔ macro-activity linker.
 * Manual mode: WBS tree picker → link N selected marks to one activity.
 * Cognara mode: Cognara proposes per-mark best-fit activity → human approves.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { X, Loader2, Check, XCircle, Sparkles, Link2, Search } from 'lucide-react';

export default function SWMMarkActivityLinker({ projectId, structuralTag, initialSelection, mode, onClose, onDone }) {
  // mode: 'manual' | 'cognara'
  const [activities, setActivities] = useState([]);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);

  // cognara state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [rationale, setRationale] = useState('');
  const [decisions, setDecisions] = useState({}); // {markNumber: {decision, activityId}}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/swm/cognara/macro-activities`);
        setActivities(data.activities || []);
      } catch { toast.error('Failed to load macro activities'); }
    })();
  }, [projectId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return activities.slice(0, 500);
    return activities.filter(a => (a.wbs || '').toLowerCase().includes(s) || (a.name || '').toLowerCase().includes(s)).slice(0, 500);
  }, [activities, q]);

  const runCognara = useCallback(async () => {
    setSuggestBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(structuralTag)}/cognara-mark-links`);
      setSuggestions(data.suggestions || []);
      setRationale(data.rationale || '');
      // default decision = APPROVE with the suggested activityId
      const seed = {};
      (data.suggestions || []).forEach(s => { seed[s.markNumber] = { decision: 'APPROVE', activityId: s.activityId }; });
      setDecisions(seed);
      toast.success(`Cognara proposed links for ${data.suggestions?.length || 0} marks`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Cognara failed'); }
    finally { setSuggestBusy(false); }
  }, [projectId, structuralTag]);

  useEffect(() => { if (mode === 'cognara' && suggestions.length === 0) runCognara(); }, [mode, suggestions.length, runCognara]);

  const doManual = async () => {
    if (!picked) { toast.error('Pick an activity'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/marks/link-activity`, {
        structuralTag, markNumbers: initialSelection, macroActivityId: picked.id,
      });
      toast.success(`Linked ${data.modified} mark${data.modified === 1 ? '' : 's'} to ${picked.name}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
    finally { setBusy(false); }
  };

  const doApprove = async () => {
    const rows = Object.entries(decisions).map(([mno, d]) => ({ markNumber: mno, decision: d.decision, activityId: d.activityId }));
    if (!rows.length) { toast.error('No decisions to submit'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/marks/approve-mark-links`, {
        structuralTag, decisions: rows,
      });
      toast.success(`${data.approved} linked · ${data.rejected} rejected`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };

  const activityLabel = (id) => {
    const a = activities.find(x => x.id === id);
    return a ? `${a.wbs} · ${a.name}` : '(unknown)';
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(10,14,26,0.65)' }} data-testid="swm-mark-linker">
      <div className="w-full max-w-3xl max-h-[90vh] rounded-xl overflow-hidden shadow-2xl flex flex-col" style={{ background: 'var(--card-bg)' }}>
        <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {mode === 'manual'
              ? `Link ${initialSelection.length} mark${initialSelection.length === 1 ? '' : 's'} to a macro activity`
              : `Cognara-suggested activity per mark · ${structuralTag}`}
          </div>
          <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {mode === 'manual'
              ? 'Only the selected marks are linked — the parent tag stays independent.'
              : 'Cognara reads each mark\'s section / elevation / weight and matches to the best-fit activity. Review below.'}
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {mode === 'manual' ? (
            <>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}/>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search WBS / activity name…"
                  className="w-full pl-8 pr-2 py-1.5 rounded border text-[11px]"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="swm-mark-linker-search"/>
              </div>
              <div className="rounded border overflow-hidden max-h-[45vh] overflow-y-auto" style={{ borderColor: 'var(--border-color)' }}>
                {filtered.map(a => {
                  const active = picked?.id === a.id;
                  const canPick = !a.isSummary && !a.isMilestone;
                  return (
                    <button key={a.id} onClick={() => canPick && setPicked(a)} disabled={!canPick}
                      className="w-full text-left px-3 py-1.5 border-b flex items-center gap-2 hover:bg-slate-50/60 disabled:cursor-default"
                      style={{ borderColor: 'var(--border-color)',
                               background: active ? 'rgba(242,107,31,0.14)' : (a.isSummary ? 'rgba(148,163,184,0.05)' : 'transparent'),
                               paddingLeft: `${12 + (a.level || 0) * 18}px` }}
                      data-testid={`swm-mark-linker-activity-${a.id}`}>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0"
                        style={{ background: 'rgba(20,58,107,0.10)', color: '#143A6B', fontWeight: a.isSummary ? 700 : 500 }}>{a.wbs}</span>
                      <span className={`text-[11.5px] flex-1 truncate ${a.isSummary ? 'font-semibold uppercase tracking-wide' : ''}`}
                        style={{ color: 'var(--text-primary)' }}>{a.name}</span>
                      {a.isSummary && <span className="text-[9px] uppercase" style={{ color: 'var(--text-muted)' }}>Summary</span>}
                      <span className="text-[10px] font-mono shrink-0" style={{ color: 'var(--text-muted)' }}>
                        {(a.plannedStart || '').slice(0, 10)} → {(a.plannedFinish || '').slice(0, 10)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {suggestBusy && (
                <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cognara analysing marks and matching against 73 SO-97.16 activities…
                </div>
              )}
              {rationale && (
                <div className="p-2.5 rounded ring-1 text-[11px]" style={{ background: 'rgba(242,107,31,0.08)', borderColor: 'rgba(242,107,31,0.4)', color: 'var(--text-primary)' }}>
                  <b>Cognara rationale:</b> {rationale}
                </div>
              )}
              <div className="rounded border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                <table className="w-full text-[11px]">
                  <thead style={{ background: 'var(--card-bg-alt)' }}>
                    <tr style={{ color: 'var(--text-muted)' }}>
                      <th className="p-1.5 text-left font-medium">Mark</th>
                      <th className="p-1.5 text-left font-medium">Suggested activity</th>
                      <th className="p-1.5 text-left font-medium">Reason</th>
                      <th className="p-1.5 text-center font-medium">Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map(s => {
                      const d = decisions[s.markNumber];
                      return (
                        <tr key={s.markNumber} className="border-b" style={{ borderColor: 'var(--border-color)' }} data-testid={`swm-mark-linker-row-${s.markNumber}`}>
                          <td className="p-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{s.markNumber}</td>
                          <td className="p-1.5">
                            <select value={d?.activityId || ''}
                              onChange={e => setDecisions(prev => ({ ...prev, [s.markNumber]: { decision: 'APPROVE', activityId: e.target.value } }))}
                              className="text-[10px] px-1.5 py-0.5 rounded border bg-transparent w-full"
                              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                              <option value="">(pick activity)</option>
                              {activities.filter(a => !a.isSummary && !a.isMilestone).map(a => (
                                <option key={a.id} value={a.id}>{a.wbs} · {a.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>{s.rationale}</td>
                          <td className="p-1.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => setDecisions(prev => ({ ...prev, [s.markNumber]: { decision: 'APPROVE', activityId: d?.activityId || s.activityId } }))}
                                className="w-6 h-6 rounded flex items-center justify-center"
                                style={{ background: d?.decision === 'APPROVE' ? '#0E7C6B' : 'transparent', color: d?.decision === 'APPROVE' ? '#fff' : '#0E7C6B', border: '1px solid #0E7C6B' }}
                                title="Approve"><Check className="w-3 h-3"/></button>
                              <button onClick={() => setDecisions(prev => ({ ...prev, [s.markNumber]: { decision: 'REJECT', activityId: null } }))}
                                className="w-6 h-6 rounded flex items-center justify-center"
                                style={{ background: d?.decision === 'REJECT' ? '#EF4444' : 'transparent', color: d?.decision === 'REJECT' ? '#fff' : '#EF4444', border: '1px solid #EF4444' }}
                                title="Reject"><XCircle className="w-3 h-3"/></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="p-3 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
          <button onClick={onClose} className="px-3 py-1 rounded text-[11px]" style={{ color: 'var(--text-muted)' }} data-testid="swm-mark-linker-cancel">Cancel</button>
          {mode === 'manual' ? (
            <button onClick={doManual} disabled={busy || !picked}
              className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-medium text-white disabled:opacity-50"
              style={{ background: '#F26B1F' }} data-testid="swm-mark-linker-manual-confirm">
              {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Link2 className="w-3 h-3"/>} Link {initialSelection.length} mark{initialSelection.length === 1 ? '' : 's'}
            </button>
          ) : (
            <button onClick={doApprove} disabled={busy || Object.keys(decisions).length === 0}
              className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-medium text-white disabled:opacity-50"
              style={{ background: '#F26B1F' }} data-testid="swm-mark-linker-cognara-confirm">
              {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Check className="w-3 h-3"/>} Submit ({Object.keys(decisions).length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
